/**
 * The timekeeping module: its own nav strip, its dashboard, and the daily-report list.
 *
 * THE REPORT STATUSES ARE THE IMPORTANT PART OF THIS FILE. `draft / submitted / timekeeper_reviewed /
 * approved / rejected` are the words a foreman uses to answer "did my crew get paid for Tuesday", and
 * they are printed on four separate screens (this dashboard, the list, the review page, the header
 * bell). They are defined once, here, keyed by the STORED status value — so a translation cannot
 * drift onto a different state and no screen can disagree with another about what a report's state
 * is called.
 *
 * `TK Reviewed` is deliberately widened to `Reviewed` in this dictionary. "TK" is an internal
 * abbreviation for the timekeeper role and reads as noise even in English; the state means the
 * timekeeper has looked at it and it is now waiting on a manager.
 */
export default {
  // ── the module's own nav strip ────────────────────────────────────────────────────────────────
  navDashboard: 'Dashboard',
  navDailyReports: 'Daily Reports',
  navServiceTickets: 'Service Tickets',
  navMyHours: 'My Hours',
  navMyHoursCrew: 'My Hours / Crew',
  navMasterTimesheet: 'Master Timesheet',
  navCrewView: 'Crew View',
  navManageCrews: 'Manage Crews',
  navSites: 'Sites',
  navAssignments: 'Assignments',

  // ── report status, keyed by the STORED value ──────────────────────────────────────────────────
  statusDraft: 'Draft',
  statusSubmitted: 'Submitted',
  statusTimekeeperReviewed: 'Reviewed',
  statusApproved: 'Approved',
  statusRejected: 'Rejected',

  // ── dashboard ─────────────────────────────────────────────────────────────────────────────────
  dashboardTitle: 'Timekeeping Dashboard',
  periodAll: 'All Time',
  periodYear: 'Year',
  periodMonth: 'Month',
  periodWeek: 'Week',
  periodCustom: 'Custom',
  dateRangeTo: 'to',
  newReport: 'New Report',
  weekOf: (range) => `Week of ${range}`,
  yearOf: (year) => `Year ${year}`,
  needsAttention: (n) => `${n} report${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} attention`,
  attentionAwaitingApproval: (waiting, rejected) => `${waiting} awaiting approval, ${rejected} rejected`,
  attentionSubmitted: (submitted, rejected) => `${submitted} submitted, ${rejected} rejected`,
  review: 'Review',
  totalReports: 'Total Reports',
  employeeEntries: 'Employee Entries',
  laborHours: 'Labor Hours',
  equipmentHours: 'Equipment Hours',
  recentReports: 'Recent Reports',
  viewAll: 'View All',
  noReportsForPeriod: 'No reports for this period',
  activeCrews: 'Active Crews',
  manage: 'Manage',
  noActiveCrews: 'No active crews',
  noForeman: 'No foreman',
  crewLine: (name, members) => `${name} · ${members} member${members === 1 ? '' : 's'}`,
  reportLine: (employees, hours) => `${employees} employee${employees === 1 ? '' : 's'} · ${hours}h labor`,
  disputesFlag: 'disputes',

  // ── the daily-report list ─────────────────────────────────────────────────────────────────────
  dailyReportsTitle: 'Daily Reports',
  myReports: 'My Reports',
  needsReview: 'Needs Review',
  allStatuses: 'All Statuses',
  allReviewable: 'All Reviewable',
  reportCount: (n) => `${n} report${n === 1 ? '' : 's'}`,
  noReportsFound: 'No reports found',
  createFirstReport: 'Create your first daily report',
  workerCount: (n) => `${n} worker${n === 1 ? '' : 's'}`,
  ticketCount: (n) => `${n} ticket${n === 1 ? '' : 's'}`,
  disputeCount: (n) => `${n} Dispute${n === 1 ? '' : 's'}`,
  disputedBlocks: (n) => `${n} disputed time block${n === 1 ? '' : 's'}`,

  // ── the reviewer's dispute queue ────────────────────────────────────────────────────────
  disputesTab: 'Disputes',
  disputesOpen: (n) => `Open (${n})`,
  disputesResolved: (n) => `Resolved (${n})`,
  noOpenDisputes: 'Nobody has disputed their hours.',
  noResolvedDisputes: 'No dispute has been resolved yet.',
  // A 403 that reads as "no disputes" is the exact failure this screen exists to prevent.
  disputesForbidden: 'You do not have permission to review disputed hours, so this queue was not loaded. It is not empty.',
  disputesUnreadable: 'The dispute queue could not be read. It is not empty — try again.',
  disputeStateOpen: 'Open',
  disputeStateResolved: 'Resolved',
  disputeHoursRecorded: (h) => `${Number(h).toFixed(1)}h recorded`,
  disputeHoursChanged: (was, now) =>
    `${Number(was).toFixed(1)}h recorded, now ${Number(now).toFixed(1)}h`,
  disputeResolutionNote: 'Resolution',
  noProject: 'No project',
  prev: 'Prev',
  next: 'Next',
  pageOf: (page, total) => `Page ${page} of ${total}`,
}
